import { apiClient } from './apiClient';

// --- Types ---

export interface ContractParty {
  id: string;
  contract_id: string;
  name: string;
  role: string;
  contact_info: Record<string, unknown> | null;
  source_page: number | null;
  confidence: number | null;
  created_at: string;
}

export interface Contract {
  id: string;
  tenant_id: string;
  site_id: string | null;
  document_id: string | null;
  contract_type: string;
  title: string;
  description: string | null;
  contract_number: string | null;
  status: string;
  effective_date: string | null;
  expiration_date: string | null;
  classification_confidence: number | null;
  classification_method: string | null;
  meta: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ContractDetail extends Contract {
  parties: ContractParty[];
  term_count: number;
  key_date_count: number;
  key_term_count: number;
  clause_count: number;
  qa_pass_count: number;
  qa_fail_count: number;
}

export interface ContractListResponse {
  items: Contract[];
  total: number;
  limit: number;
  offset: number;
}

export interface ExtractedTerm {
  id: string;
  contract_id: string;
  template_field_id: string | null;
  field_key: string;
  field_label: string;
  value_text: string | null;
  value_number: number | null;
  value_date: string | null;
  value_json: Record<string, unknown> | null;
  confidence: number | null;
  extraction_method: string | null;
  needs_review: boolean;
  source_page: number | null;
  source_excerpt: string | null;
  meta: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ExtractedTermListResponse {
  items: ExtractedTerm[];
  total: number;
}

export interface ContractKeyDate {
  id: string;
  tenant_id: string;
  contract_id: string;
  date_type: string;
  label: string;
  date_value: string;
  description: string | null;
  alert_days_before: number | null;
  is_recurring: boolean;
  confidence: number | null;
  source_page: number | null;
  created_at: string;
  contract_title?: string | null;
  contract_type?: string | null;
}

export interface ContractKeyTerm {
  id: string;
  tenant_id: string;
  contract_id: string;
  term_type: string;
  label: string;
  value_text: string | null;
  value_number: number | null;
  unit: string | null;
  currency: string | null;
  effective_from: string | null;
  effective_to: string | null;
  description: string | null;
  confidence: number | null;
  source_page: number | null;
  created_at: string;
  contract_title?: string | null;
  contract_type?: string | null;
}

export interface ContractClause {
  id: string;
  tenant_id: string;
  contract_id: string;
  clause_type: string;
  label: string;
  condition: string | null;
  consequence: string | null;
  full_text: string | null;
  parameters: Record<string, unknown> | null;
  section_reference: string | null;
  confidence: number | null;
  source_page: number | null;
  created_at: string;
  contract_title?: string | null;
  contract_type?: string | null;
}

export interface ContractQAResult {
  id: string;
  contract_id: string;
  qa_check_id: string;
  processing_job_id: string;
  answer_text: string | null;
  answer_boolean: boolean | null;
  passed: boolean | null;
  confidence: number | null;
  reasoning: string | null;
  source_page: number | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  check_key: string | null;
  check_label: string | null;
  severity: string | null;
}

// --- Template Types ---

export interface TemplateField {
  id: string;
  template_id: string;
  field_key: string;
  field_label: string;
  field_type: string;
  description: string | null;
  is_required: boolean;
  section_hint: string | null;
  cross_cut_target: string | null;
  cross_cut_type: string | null;
  sort_order: number;
  created_at: string;
}

export interface TemplateQACheck {
  id: string;
  template_id: string;
  check_key: string;
  check_label: string;
  check_type: string;
  question_prompt: string;
  requires_related_docs: boolean;
  expected_answer_type: string;
  severity: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
}

export interface ExtractionTemplate {
  id: string;
  tenant_id: string | null;
  contract_type: string;
  name: string;
  description: string | null;
  version: number;
  is_active: boolean;
  extraction_prompt: string | null;
  evaluation_prompt: string | null;
  meta: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ExtractionTemplateDetail extends ExtractionTemplate {
  fields: TemplateField[];
  qa_checks: TemplateQACheck[];
}

export interface TemplateListResponse {
  items: ExtractionTemplate[];
  total: number;
  limit: number;
  offset: number;
}

// --- API ---

export const contractsApi = apiClient.injectEndpoints({
  endpoints: (builder) => ({
    listSiteContracts: builder.query<ContractListResponse, { siteId: string; contractType?: string; status?: string; limit?: number; offset?: number }>({
      query: ({ siteId, ...filters }) => {
        const params = new URLSearchParams({ site_id: siteId });
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key === 'contractType' ? 'contract_type' : key, String(value));
          }
        });
        return { url: `/api/v1/contracts?${params.toString()}`, method: 'GET' };
      },
      providesTags: (result, _error, { siteId }) =>
        result
          ? [
              ...result.items.map(({ id }) => ({ type: 'Contract' as const, id })),
              { type: 'Contract' as const, id: `LIST-${siteId}` },
            ]
          : [{ type: 'Contract' as const, id: `LIST-${siteId}` }],
    }),

    getContract: builder.query<ContractDetail, { contractId: string }>({
      query: ({ contractId }) => ({ url: `/api/v1/contracts/${contractId}`, method: 'GET' }),
      providesTags: (_result, _error, { contractId }) => [{ type: 'Contract' as const, id: contractId }],
    }),

    getContractTerms: builder.query<ExtractedTermListResponse, { contractId: string }>({
      query: ({ contractId }) => ({ url: `/api/v1/contracts/${contractId}/terms`, method: 'GET' }),
    }),

    getContractKeyDates: builder.query<{ items: ContractKeyDate[]; total: number }, { contractId: string }>({
      query: ({ contractId }) => ({ url: `/api/v1/contracts/${contractId}/key-dates`, method: 'GET' }),
    }),

    getContractKeyTerms: builder.query<{ items: ContractKeyTerm[]; total: number }, { contractId: string }>({
      query: ({ contractId }) => ({ url: `/api/v1/contracts/${contractId}/key-terms`, method: 'GET' }),
    }),

    getContractClauses: builder.query<{ items: ContractClause[]; total: number }, { contractId: string }>({
      query: ({ contractId }) => ({ url: `/api/v1/contracts/${contractId}/clauses`, method: 'GET' }),
    }),

    getContractParties: builder.query<{ items: ContractParty[]; total: number }, { contractId: string }>({
      query: ({ contractId }) => ({ url: `/api/v1/contracts/${contractId}/parties`, method: 'GET' }),
    }),

    getContractQAResults: builder.query<{ items: ContractQAResult[]; total: number }, { contractId: string }>({
      query: ({ contractId }) => ({ url: `/api/v1/contracts/${contractId}/qa-results`, method: 'GET' }),
    }),

    // Template endpoints
    listTemplates: builder.query<TemplateListResponse, { contractType?: string; isActive?: boolean }>({
      query: (filters) => {
        const params = new URLSearchParams();
        if (filters.contractType) params.append('contract_type', filters.contractType);
        if (filters.isActive !== undefined) params.append('is_active', String(filters.isActive));
        return { url: `/api/v1/contracts/templates?${params.toString()}`, method: 'GET' };
      },
    }),

    getTemplate: builder.query<ExtractionTemplateDetail, { templateId: string }>({
      query: ({ templateId }) => ({ url: `/api/v1/contracts/templates/${templateId}`, method: 'GET' }),
    }),

    updateTemplate: builder.mutation<ExtractionTemplate, { templateId: string; body: Partial<ExtractionTemplate> }>({
      query: ({ templateId, body }) => ({
        url: `/api/v1/contracts/templates/${templateId}`,
        method: 'PUT',
        body,
      }),
    }),

    // Cross-cutting queries
    getUpcomingDates: builder.query<{ items: Array<{ key_date: ContractKeyDate; days_until: number }>; total: number }, { siteId?: string; days?: number }>({
      query: ({ siteId, days = 90 }) => {
        const params = new URLSearchParams({ days: String(days) });
        if (siteId) params.append('site_id', siteId);
        return { url: `/api/v1/contracts/upcoming-dates?${params.toString()}`, method: 'GET' };
      },
    }),
  }),
  overrideExisting: true,
});

export const {
  useListSiteContractsQuery,
  useGetContractQuery,
  useGetContractTermsQuery,
  useGetContractKeyDatesQuery,
  useGetContractKeyTermsQuery,
  useGetContractClausesQuery,
  useGetContractPartiesQuery,
  useGetContractQAResultsQuery,
  useListTemplatesQuery,
  useGetTemplateQuery,
  useUpdateTemplateMutation,
  useGetUpcomingDatesQuery,
} = contractsApi;
